/**
 * HRBulkImport.jsx
 * Bulk-import candidates into the HR tracker from:
 *   1. HTML ranking files (same format as the existing resume/*.html files)
 *   2. CSV (downloadable template)
 *   3. Bulk CV file upload → uploaded to GitHub via server proxy
 *
 * Admins can preview, assign category, handle duplicates, then merge.
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  Upload, FileText, Download, Check, AlertCircle, X,
  ChevronDown, ChevronUp, RefreshCw, Tag, Trash2, Plus
} from 'lucide-react';
import { Button } from './ui/button';
import { CANDIDATE_CATEGORIES, CANDIDATE_PIPELINE_STATUSES } from '../utils/hrGithubStorage';

// ── CSV template columns ──────────────────────────────────────────────────────

const CSV_HEADERS = [
  'name','phone','email','location','experience_years',
  'score','grade','category','justification','cv_filename','source_file'
];

const CSV_SAMPLE = [
  'John Smith','(555) 123-4567','john@example.com','New York, NY',
  '8','85','A','java','8+ yrs; Spring Boot microservices, Kafka, AWS EKS; strong backend engineer',
  'JohnSmith_Resume.pdf',''
].join(',');

const GRADE_OPTIONS = ['A','B','C','D'];

// ── Helper: parse the D=[...] array from HTML ranking files ──────────────────

function parseHtmlFile(html) {
  // Match both `const D=[` and `var D=[` patterns
  const match = html.match(/(?:const|var|let)\s+D\s*=\s*(\[[\s\S]*?\]);/);
  if (!match) throw new Error('No candidate data array (D=[...]) found. Make sure you are uploading one of the HTML ranking files.');
  try {
    // eslint-disable-next-line no-new-func
    const data = Function('"use strict"; return ' + match[1])();

    // Optional revised HTML field map: const FILEMAP = { "Candidate Name": "Resume.pdf" }
    let fileMap = {};
    const fileMapMatch = html.match(/(?:const|var|let)\s+FILEMAP\s*=\s*(\{[\s\S]*?\});/);
    if (fileMapMatch) {
      // eslint-disable-next-line no-new-func
      const parsed = Function('"use strict"; return ' + fileMapMatch[1])();
      if (parsed && typeof parsed === 'object') fileMap = parsed;
    }

    return data.map((item, idx) => ({
      rank:         item.r ?? idx + 1,
      name:         (item.n || '').trim(),
      phone:        (item.p || 'N/A').trim(),
      email:        (item.e || 'N/A').trim(),
      location:     (item.l || 'N/A').trim(),
      experience:   Number(item.x) || 0,
      score:        Number(item.s) || 0,
      grade:        (item.g || 'C').toUpperCase(),
      justification:(item.j || '').trim(),
      category:     'java',   // default — user can override
      cvFile:       (item.f || item.cv_filename || item.source_file || fileMap[item.n] || null)
    }));
  } catch (e) {
    throw new Error('Failed to parse HTML file: ' + e.message);
  }
}

// ── Helper: parse CSV ─────────────────────────────────────────────────────────

function parseCsvFile(text) {
  const lines = text.trim().split('\n').filter(l => l.trim() && !l.startsWith('#'));
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row.');
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g,'_'));
  const idx = (key) => headers.indexOf(key);
  const firstPresent = (...keys) => {
    for (const k of keys) {
      const i = idx(k);
      if (i !== -1) return i;
    }
    return -1;
  };

  return lines.slice(1).map((line, i) => {
    // Basic CSV parse (handles simple quoted fields)
    const cols = parseCsvLine(line);
    const get  = (key, def = '') => (cols[idx(key)] || '').trim() || def;
    const name = get('name');
    if (!name) return null;
    const cvIdx = firstPresent('cv_filename', 'source_file', 'source_filename', 'file_name', 'filename', 'resume_file');
    const cvVal = cvIdx >= 0 ? (cols[cvIdx] || '').trim() : '';
    return {
      rank:         i + 1,
      name,
      phone:        get('phone', 'N/A'),
      email:        get('email', 'N/A'),
      location:     get('location', 'N/A'),
      experience:   Number(get('experience_years', '0')) || 0,
      score:        Number(get('score', '70')) || 70,
      grade:        get('grade', 'B').toUpperCase(),
      category:     get('category', 'java'),
      justification:get('justification', ''),
      cvFile:       cvVal || null
    };
  }).filter(Boolean);
}

function parseCsvLine(line) {
  const cols = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ',' && !inQ) { cols.push(cur); cur = ''; }
    else { cur += ch; }
  }
  cols.push(cur);
  return cols;
}

// ── Helper: normalize string for fuzzy match ────────────────────────────────

function normalizeForMatch(str) {
  return str
    .toLowerCase()
    .replace(/\.[^.]+$/, '')           // strip extension
    .replace(/[_\-]+/g, ' ')           // underscores / hyphens → space
    .replace(/\s*\(\d+\)\s*/g, ' ')    // remove "(1)", "(2)" copies
    .replace(/[^a-z0-9 ]/g, '')        // strip remaining punctuation
    .replace(/\s+/g, ' ')
    .trim();
}

const NOISE_WORDS = new Set([
  'resume','resumes','cv','profile','latest','final','main','new',
  'java','developer','engineer','full','stack','fullstack','sr','senior','lead',
  'version','copy','updated'
]);

function cleanTokens(str) {
  return normalizeForMatch(str)
    .split(' ')
    .filter(w => w.length > 1 && !NOISE_WORDS.has(w));
}

function compact(str) {
  return normalizeForMatch(str).replace(/\s+/g, '');
}

function normalizeFilenameKey(filename, stripExtension = false) {
  let s = (filename || '').toLowerCase().trim();
  if (stripExtension) s = s.replace(/\.[^.]+$/, '');
  return s
    .replace(/[_\-\s]+/g, '')
    .replace(/\(\d+\)/g, '')
    .replace(/[^a-z0-9.]/g, '');
}

function buildCandidateFileMaps(candidates) {
  const byExact = new Map();
  const byNoExt = new Map();
  for (const c of candidates) {
    if (!c.cvFile || c.cvFile === 'N/A') continue;
    const k1 = normalizeFilenameKey(c.cvFile, false);
    const k2 = normalizeFilenameKey(c.cvFile, true);
    if (k1 && !byExact.has(k1)) byExact.set(k1, c);
    if (k2 && !byNoExt.has(k2)) byNoExt.set(k2, c);
  }
  return { byExact, byNoExt };
}

// ── Helper: fuzzy-match a filename to the closest candidate ─────────────────
// Returns { candidate, confidence 0-1 } or null if no strong match

function fuzzyMatchCandidate(filename, candidates) {
  const normFile = normalizeForMatch(filename);
  const fileWords = cleanTokens(normFile);
  const compactFile = compact(normFile);
  if (fileWords.length === 0) return null;

  let bestScore = 0;
  let bestCandidate = null;

  for (const c of candidates) {
    const normName = normalizeForMatch(c.name);
    const nameWords = cleanTokens(normName);
    const compactName = compact(normName);

    // High-confidence direct string containment in compact form
    let score = 0;
    if (compactFile.includes(compactName) || compactName.includes(compactFile)) {
      score = 0.98;
    }

    // Email local-part hit (e.g., akshitha.reddy in filename)
    if (c.email && c.email !== 'N/A') {
      const emailLocal = normalizeForMatch(c.email.split('@')[0]).replace(/\s+/g, '');
      if (emailLocal && compactFile.includes(emailLocal)) {
        score = Math.max(score, 0.95);
      }
    }

    // Word-overlap score
    const hits = nameWords.filter(nw =>
      fileWords.some(fw => fw.includes(nw) || nw.includes(fw))
    );
    const overlapScore = nameWords.length > 0
      ? hits.length / Math.max(nameWords.length, fileWords.length)
      : 0;

    // Blend overlap with exact compact hints
    score = Math.max(score, overlapScore);

    if (score > bestScore) {
      bestScore = score;
      bestCandidate = c;
    }
  }

  // Require at least 34% overlap/hint to avoid false positives while improving recall
  return bestScore >= 0.34 ? { candidate: bestCandidate, confidence: bestScore } : null;
}

// ── Helper: file icon by extension ──────────────────────────────────────────

function fileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (ext === 'pdf')  return '📄';
  if (ext === 'zip')  return '🗜️';
  if (ext === 'html' || ext === 'htm') return '🌐';
  return '📝';
}

// ── Helper: generate unique candidate ID ────────────────────────────────────

function generateId(existingIds) {
  let n = existingIds.length + 1;
  while (existingIds.includes(`c${String(n).padStart(3,'0')}`)) n++;
  return `c${String(n).padStart(3,'0')}`;
}

// ── CV Upload helpers ────────────────────────────────────────────────────────

// Reads a Blob as base64 string (data portion only)
function readBlobAsBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(',')[1]);
    reader.onerror = () => reject(new Error('File read error'));
    reader.readAsDataURL(blob);
  });
}

// Parse server response safely — if not JSON, surface a clear message
async function parseUploadResponse(res) {
  const text = await res.text();
  try { return { ok: res.ok, status: res.status, json: JSON.parse(text) }; }
  catch {
    // Server returned HTML (likely 413 Payload Too Large from proxy)
    if (res.status === 413) throw new Error('File too large for server proxy — try a smaller file');
    throw new Error(`Server error (${res.status}) — unexpected response`);
  }
}

const CV_CHUNK_SIZE = 150 * 1024; // smaller chunks avoid proxy/body edge cases

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function postUploadPayload(payload, maxRetries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const parsed = await parseUploadResponse(
        await fetch('/api/hr/cv/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
      );
      if (!parsed.ok) {
        const msg = parsed.json?.error || `Upload failed (${parsed.status})`;
        // Retry transient server responses
        if (parsed.status >= 500 && attempt < maxRetries) {
          await sleep(250 * (attempt + 1));
          continue;
        }
        throw new Error(msg);
      }
      return parsed.json;
    } catch (err) {
      lastErr = err;
      if (attempt < maxRetries) {
        await sleep(250 * (attempt + 1));
        continue;
      }
    }
  }
  throw lastErr || new Error('Upload failed');
}

async function uploadCVToServer(file, onProgress) {
  if (file.size <= CV_CHUNK_SIZE) {
    // ── Single upload ──────────────────────────────────────────────────────
    const base64 = await readBlobAsBase64(file);
    const json = await postUploadPayload({ filename: file.name, content: base64 });
    return json.filename || file.name;
  }

  // ── Chunked upload (large files) ─────────────────────────────────────────
  const total = Math.ceil(file.size / CV_CHUNK_SIZE);
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  for (let i = 0; i < total; i++) {
    const start = i * CV_CHUNK_SIZE;
    const blob  = file.slice(start, Math.min(start + CV_CHUNK_SIZE, file.size));
    const base64 = await readBlobAsBase64(blob);

    await postUploadPayload({
      filename: file.name,
      content: base64,
      chunk: { index: i, total, uploadId }
    });
    if (onProgress) onProgress(i + 1, total);
  }
  return file.name;
}

// ── Download CSV template ────────────────────────────────────────────────────

function downloadCsvTemplate() {
  const catList = CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c => c.value).join('|');
  const gradeList = GRADE_OPTIONS.join('|');
  const content = [
    '# HR Resourcing Bulk Import Template',
    `# category options: ${catList}`,
    `# grade options: ${gradeList}  (A=85+ Excellent, B=70-84 Strong, C=55-69 Good, D=<55 Below avg)`,
    `# score: numeric 0-100`,
    `# experience_years: numeric`,
    `# cv_filename or source_file: exact filename of the CV to map directly (e.g. JohnSmith.pdf) — leave blank if no CV`,
    CSV_HEADERS.join(','),
    CSV_SAMPLE
  ].join('\n');
  const blob = new Blob([content], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'hr_candidates_import_template.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── PreviewTable ─────────────────────────────────────────────────────────────

function PreviewTable({ rows, onUpdateRow, onRemoveRow, existingNames }) {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr>
            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 w-6">#</th>
            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 min-w-[130px]">Name</th>
            <th className="border-b border-gray-200 px-2 py-2 text-center font-semibold text-gray-600 w-14">Score</th>
            <th className="border-b border-gray-200 px-2 py-2 text-center font-semibold text-gray-600 w-10">Exp</th>
            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 min-w-[120px]">Category</th>
            <th className="border-b border-gray-200 px-2 py-2 text-left font-semibold text-gray-600 min-w-[110px]">CV File</th>
            <th className="border-b border-gray-200 px-2 py-2 text-center font-semibold text-gray-600 w-16">Status</th>
            <th className="border-b border-gray-200 px-2 py-2 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const isDup = existingNames.includes(row.name.toLowerCase());
            return (
              <tr key={idx} className={`${isDup ? 'bg-yellow-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <td className="border-b border-gray-100 px-2 py-1.5 text-gray-400">{row.rank}</td>
                <td className="border-b border-gray-100 px-2 py-1.5">
                  <span className="font-medium text-gray-900">{row.name}</span>
                  {isDup && <span className="ml-1 text-yellow-600 text-xs italic">(exists)</span>}
                </td>
                <td className="border-b border-gray-100 px-2 py-1.5 text-center">
                  <span className={`px-1.5 py-0.5 rounded-full font-bold ${
                    row.grade === 'A' ? 'bg-green-100 text-green-800' :
                    row.grade === 'B' ? 'bg-blue-100 text-blue-800' :
                    row.grade === 'C' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>{row.score} {row.grade}</span>
                </td>
                <td className="border-b border-gray-100 px-2 py-1.5 text-center text-gray-600">{row.experience > 0 ? `${row.experience}y` : '—'}</td>
                <td className="border-b border-gray-100 px-2 py-1.5">
                  <select value={row.category || 'java'}
                    onChange={e => onUpdateRow(idx, { category: e.target.value })}
                    className="text-xs border rounded px-1 py-0.5 w-full">
                    {CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c =>
                      <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </td>
                <td className="border-b border-gray-100 px-2 py-1.5">
                  <input type="text" value={row.cvFile || ''}
                    onChange={e => onUpdateRow(idx, { cvFile: e.target.value || null })}
                    placeholder="filename.pdf"
                    className="text-xs border rounded px-1 py-0.5 w-full font-mono" />
                </td>
                <td className="border-b border-gray-100 px-2 py-1.5 text-center">
                  {isDup
                    ? <span className="text-yellow-600 font-medium">Update</span>
                    : <span className="text-green-600 font-medium">New</span>}
                </td>
                <td className="border-b border-gray-100 px-2 py-1.5 text-center">
                  <button onClick={() => onRemoveRow(idx)} className="text-red-400 hover:text-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const HRBulkImport = ({ candidates, candidatesSha, onImportComplete }) => {
  const [activeTab,      setActiveTab]      = useState('html');   // html | csv | cv
  const [previewRows,    setPreviewRows]    = useState([]);
  const [importing,      setImporting]      = useState(false);
  const [parseError,     setParseError]     = useState('');
  const [importResult,   setImportResult]   = useState(null);  // { added, updated }
  const [bulkCategory,   setBulkCategory]   = useState('');
  const [dupMode,        setDupMode]        = useState('skip'); // skip | overwrite
  const [cvFiles,        setCvFiles]        = useState([]);    // File[]
  const [cvUploadStatus, setCvUploadStatus] = useState([]);   // { name, status, error }
  const [cvUploading,    setCvUploading]    = useState(false);
  // Auto-map state
  const [autoMapResults, setAutoMapResults] = useState([]);   // { filename, candidate, confidence, accepted }
  const [autoMapApplying, setAutoMapApplying] = useState(false);
  const [autoMapDone,    setAutoMapDone]    = useState(false);
  const [autoAppliedCount, setAutoAppliedCount] = useState(0);

  const htmlInputRef = useRef();
  const csvInputRef  = useRef();
  const cvInputRef   = useRef();

  const existingNames = candidates.map(c => c.name.toLowerCase());

  // ── Parse handlers ─────────────────────────────────────────────────────────

  const handleHtmlFile = useCallback((file) => {
    setParseError('');
    setPreviewRows([]);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseHtmlFile(e.target.result);
        if (rows.length === 0) { setParseError('No candidates found in file.'); return; }
        setPreviewRows(rows);
      } catch (err) { setParseError(err.message); }
    };
    reader.readAsText(file);
  }, []);

  const handleCsvFile = useCallback((file) => {
    setParseError('');
    setPreviewRows([]);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCsvFile(e.target.result);
        if (rows.length === 0) { setParseError('No valid rows found in CSV.'); return; }
        setPreviewRows(rows);
      } catch (err) { setParseError(err.message); }
    };
    reader.readAsText(file);
  }, []);

  // ── Bulk category apply ────────────────────────────────────────────────────

  const applyBulkCategory = () => {
    if (!bulkCategory) return;
    setPreviewRows(prev => prev.map(r => ({ ...r, category: bulkCategory })));
  };

  // ── Update / remove row ────────────────────────────────────────────────────

  const updateRow = (idx, updates) => {
    setPreviewRows(prev => prev.map((r, i) => i === idx ? { ...r, ...updates } : r));
  };

  const removeRow = (idx) => {
    setPreviewRows(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Import candidates ──────────────────────────────────────────────────────

  const handleImport = async () => {
    if (previewRows.length === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      const existingIds = candidates.map(c => c.id);
      let added = 0, updated = 0;
      const newList = [...candidates];

      for (const row of previewRows) {
        const existIdx = newList.findIndex(c => c.name.toLowerCase() === row.name.toLowerCase());
        const today = new Date().toISOString().split('T')[0];
        if (existIdx >= 0) {
          if (dupMode === 'overwrite') {
            newList[existIdx] = {
              ...newList[existIdx],
              score:        row.score,
              grade:        row.grade,
              experience:   row.experience,
              phone:        row.phone !== 'N/A' ? row.phone : newList[existIdx].phone,
              email:        row.email !== 'N/A' ? row.email : newList[existIdx].email,
              location:     row.location !== 'N/A' ? row.location : newList[existIdx].location,
              justification:row.justification || newList[existIdx].justification,
              category:     row.category || newList[existIdx].category,
              cvFile:       row.cvFile || newList[existIdx].cvFile,
              updatedDate:  today
            };
            updated++;
          } else {
            // In skip mode, still apply direct cv/category mapping from HTML/CSV if missing.
            const existing = newList[existIdx];
            const patch = {};
            if (row.cvFile && row.cvFile !== existing.cvFile) patch.cvFile = row.cvFile;
            if (row.category && !existing.category) patch.category = row.category;
            if (Object.keys(patch).length > 0) {
              newList[existIdx] = { ...existing, ...patch, updatedDate: today };
              updated++;
            }
          }
          // else skip
        } else {
          const id = generateId(newList.map(c => c.id));
          newList.push({
            id,
            rank:          row.rank,
            name:          row.name,
            phone:         row.phone,
            email:         row.email,
            location:      row.location,
            experience:    row.experience,
            score:         row.score,
            grade:         row.grade,
            category:      row.category || 'java',
            justification: row.justification,
            cvFile:        row.cvFile || null,
            source:        'bulk-import',
            addedDate:     today,
            status:        'available',
            proposals:     []
          });
          added++;
        }
      }

      await onImportComplete(newList, candidatesSha);
      setImportResult({ added, updated });
      setPreviewRows([]);
    } catch (e) {
      setParseError('Import failed: ' + e.message);
    } finally {
      setImporting(false);
    }
  };

  // ── CV upload ──────────────────────────────────────────────────────────────

  const handleCvUpload = async () => {
    if (cvFiles.length === 0) return;
    setCvUploading(true);
    setAutoMapResults([]);
    setAutoMapDone(false);
    setAutoAppliedCount(0);
    const results = cvFiles.map(f => ({ name: f.name, status: 'pending', error: '' }));
    setCvUploadStatus([...results]);

    for (let i = 0; i < cvFiles.length; i++) {
      try {
        await uploadCVToServer(cvFiles[i], (done, total) => {
          results[i] = { name: cvFiles[i].name, status: 'pending', error: '', chunkProgress: `${done}/${total} chunks` };
          setCvUploadStatus([...results]);
        });
        results[i] = { name: cvFiles[i].name, status: 'done', error: '' };
      } catch (e) {
        results[i] = { name: cvFiles[i].name, status: 'error', error: e.message };
      }
      setCvUploadStatus([...results]);
    }
    setCvUploading(false);

    // Run auto-map on successfully uploaded files
    const uploaded = results
      .filter(r => r.status === 'done')
      .map(r => r.name)
      .filter(name => /\.(pdf|doc|docx)$/i.test(name));
    if (uploaded.length > 0 && candidates.length > 0) {
      const { byExact, byNoExt } = buildCandidateFileMaps(candidates);
      const mapped = uploaded.map(filename => {
        const direct =
          byExact.get(normalizeFilenameKey(filename, false)) ||
          byNoExt.get(normalizeFilenameKey(filename, true));

        if (direct) {
          return {
            filename,
            candidate: direct,
            confidence: 1,
            accepted: true,
            matchType: 'direct'
          };
        }

        const match = fuzzyMatchCandidate(filename, candidates);
        return {
          filename,
          candidate:  match ? match.candidate : null,
          confidence: match ? match.confidence : 0,
          accepted:   match && match.confidence >= 0.5,
          matchType:  match ? 'fuzzy' : 'none'
        };
      });

      // Auto-apply only strict direct matches so Candidates tab updates immediately.
      const directToApply = mapped.filter(r => r.matchType === 'direct' && r.candidate);
      if (directToApply.length > 0) {
        try {
          const today = new Date().toISOString().split('T')[0];
          const updated = candidates.map(c => {
            const hit = directToApply.find(r => r.candidate.id === c.id);
            if (!hit) return c;
            return { ...c, cvFile: hit.filename, updatedDate: today };
          });
          await onImportComplete(updated, candidatesSha);
          setAutoMapDone(true);
          setAutoAppliedCount(directToApply.length);
          // Keep only non-direct matches for optional manual review.
          setAutoMapResults(mapped.filter(r => r.matchType !== 'direct'));
        } catch (e) {
          setParseError('Auto-map save failed: ' + e.message);
          setAutoMapResults(mapped);
        }
      } else {
        setAutoMapResults(mapped);
      }
    }
  };

  // ── Apply auto-map: write cvFile back into matched candidates ─────────────

  const applyAutoMap = async () => {
    const toApply = autoMapResults.filter(r => r.accepted && r.candidate);
    if (toApply.length === 0) return;
    setAutoMapApplying(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const updated = candidates.map(c => {
        const hit = toApply.find(r => r.candidate.id === c.id);
        if (!hit) return c;
        return { ...c, cvFile: hit.filename, updatedDate: today };
      });
      await onImportComplete(updated, candidatesSha);
      setAutoMapDone(true);
      setAutoAppliedCount(toApply.length);
      setAutoMapResults([]);
    } catch (e) {
      setParseError('Auto-map save failed: ' + e.message);
    } finally {
      setAutoMapApplying(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const TABS = [
    { id: 'html', label: 'Import from HTML', icon: '🗂',  loading: importing },
    { id: 'csv',  label: 'Import from CSV',  icon: '📋',  loading: importing },
    { id: 'cv',   label: 'Bulk CV Upload',   icon: '📎',  loading: cvUploading || autoMapApplying }
  ];

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 leading-relaxed">
        <strong>Bulk Import:</strong> Use the HTML tab to import candidates from existing ranking HTML files (same format as <code>resume/*.html</code>).
        Use the CSV tab to import from a spreadsheet (download the template first).
        Use CV Upload to add resume files that clients can view/download.
      </div>

      {/* Tab nav */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setParseError(''); setPreviewRows([]); setImportResult(null); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === t.id ? 'border-blue-600 text-blue-600 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {t.icon} {t.label}
            {t.loading && activeTab === t.id && (
              <RefreshCw className="w-3.5 h-3.5 animate-spin ml-1 text-blue-500" />
            )}
            {t.loading && activeTab !== t.id && (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse ml-1" title="Processing…" />
            )}
          </button>
        ))}
      </div>

      {/* ── HTML Import ── */}
      {activeTab === 'html' && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl text-center">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">Upload an HTML ranking file</p>
            <p className="text-xs text-gray-500 mb-3">Format: files from <code>resume/</code> folder — e.g. <code>java_candidate_final_ranking_40.html</code></p>
            <input ref={htmlInputRef} type="file" accept=".html,.htm" className="hidden"
              onChange={e => e.target.files[0] && handleHtmlFile(e.target.files[0])} />
            <Button onClick={() => htmlInputRef.current.click()} variant="outline" size="sm">
              Choose HTML File
            </Button>
          </div>

          {parseError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {parseError}
            </div>
          )}

          {importResult && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <Check className="w-4 h-4" />
              Import complete — <strong>{importResult.added}</strong> added, <strong>{importResult.updated}</strong> updated
            </div>
          )}

          {previewRows.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-700">{previewRows.length} candidates parsed</span>

                {/* Bulk category */}
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-gray-400" />
                  <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
                    className="text-xs border rounded px-2 py-1">
                    <option value="">Set all category…</option>
                    {CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c =>
                      <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <Button onClick={applyBulkCategory} variant="outline" size="sm" className="text-xs h-7">Apply</Button>
                </div>

                {/* Duplicate handling */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Duplicates:</span>
                  <select value={dupMode} onChange={e => setDupMode(e.target.value)}
                    className="text-xs border rounded px-2 py-1">
                    <option value="skip">Skip existing</option>
                    <option value="overwrite">Overwrite existing</option>
                  </select>
                </div>
              </div>

              <PreviewTable rows={previewRows} onUpdateRow={updateRow} onRemoveRow={removeRow} existingNames={existingNames} />

              <div className="flex gap-3">
                <Button onClick={handleImport} disabled={importing} className="bg-blue-600">
                  {importing ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Importing…</> : `Import ${previewRows.length} Candidates`}
                </Button>
                <Button onClick={() => setPreviewRows([])} variant="outline">Clear</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CSV Import ── */}
      {activeTab === 'csv' && (
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-1">
            <p><strong>Step 1:</strong> Download the CSV template below</p>
            <p><strong>Step 2:</strong> Fill in your candidates (one per row). The <code>category</code> column accepts: {CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c => c.value).join(', ')}</p>
            <p><strong>Step 2a:</strong> Use <code>cv_filename</code> or <code>source_file</code> to map the resume filename directly during CSV import.</p>
            <p><strong>Step 3:</strong> Upload the filled CSV file</p>
            <p><strong>Step 4:</strong> Review the preview, adjust categories, then click Import</p>
          </div>

          <Button onClick={downloadCsvTemplate} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" /> Download CSV Template
          </Button>

          <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">Upload filled CSV file</p>
            <p className="text-xs text-gray-500 mb-3">Must match the template column order</p>
            <input ref={csvInputRef} type="file" accept=".csv,.txt" className="hidden"
              onChange={e => e.target.files[0] && handleCsvFile(e.target.files[0])} />
            <Button onClick={() => csvInputRef.current.click()} variant="outline" size="sm">Choose CSV File</Button>
          </div>

          {parseError && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {parseError}
            </div>
          )}

          {importResult && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <Check className="w-4 h-4" />
              Import complete — <strong>{importResult.added}</strong> added, <strong>{importResult.updated}</strong> updated
            </div>
          )}

          {previewRows.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-700">{previewRows.length} candidates parsed</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Duplicates:</span>
                  <select value={dupMode} onChange={e => setDupMode(e.target.value)}
                    className="text-xs border rounded px-2 py-1">
                    <option value="skip">Skip existing</option>
                    <option value="overwrite">Overwrite existing</option>
                  </select>
                </div>
              </div>
              <PreviewTable rows={previewRows} onUpdateRow={updateRow} onRemoveRow={removeRow} existingNames={existingNames} />
              <div className="flex gap-3">
                <Button onClick={handleImport} disabled={importing} className="bg-blue-600">
                  {importing ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Importing…</> : `Import ${previewRows.length} Candidates`}
                </Button>
                <Button onClick={() => setPreviewRows([])} variant="outline">Clear</Button>
              </div>
            </div>
          )}

          {/* CSV format reference */}
          <details className="border border-gray-200 rounded-lg overflow-hidden">
            <summary className="px-4 py-2 bg-gray-50 cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2">
              <ChevronDown className="w-4 h-4" /> CSV Column Reference
            </summary>
            <div className="p-4 overflow-x-auto">
              <table className="text-xs border-collapse min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-3 py-1.5 text-left">Column</th>
                    <th className="border border-gray-200 px-3 py-1.5 text-left">Required</th>
                    <th className="border border-gray-200 px-3 py-1.5 text-left">Description / Allowed values</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['name',             '✅ Yes', 'Full name of the candidate'],
                    ['phone',            '— No',  'Phone number (use N/A if unknown)'],
                    ['email',            '— No',  'Email address (use N/A if unknown)'],
                    ['location',         '— No',  'City, State or N/A'],
                    ['experience_years', '— No',  'Integer years of experience (0 if unknown)'],
                    ['score',            '— No',  'Numeric 0-100 (default 70)'],
                    ['grade',            '— No',  'A | B | C | D (default B)'],
                    ['category',         '— No',  CANDIDATE_CATEGORIES.filter(c => c.value !== 'all').map(c => c.value).join(' | ')],
                    ['justification',    '— No',  'Free-text assessment / notes about the candidate'],
                    ['cv_filename',      '— No',  'Exact filename of CV to map directly (e.g. JohnSmith.pdf)'],
                    ['source_file',      '— No',  'Alias of cv_filename; either column can be used']
                  ].map(([col, req, desc]) => (
                    <tr key={col}>
                      <td className="border border-gray-200 px-3 py-1.5 font-mono text-blue-700">{col}</td>
                      <td className="border border-gray-200 px-3 py-1.5">{req}</td>
                      <td className="border border-gray-200 px-3 py-1.5 text-gray-600">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}

      {/* ── Bulk CV Upload ── */}
      {activeTab === 'cv' && (
        <div className="space-y-4">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800 space-y-1">
            <p><strong>Upload resume/CV files</strong> — these are stored on GitHub and linked to candidates.</p>
            <p>After uploading, names are <strong>auto-matched</strong> to candidates. Review matches below and click <em>Apply Mappings</em>.</p>
            <p>Or go to Candidates tab → click a candidate → enter the filename in the CV field manually.</p>
          </div>

          <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl text-center">
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">Select CV files to upload</p>
            <p className="text-xs text-gray-500 mb-3">PDF, DOC, DOCX, HTML, ZIP — multiple files — max 20 MB each</p>
            <input ref={cvInputRef} type="file"
              accept=".pdf,.doc,.docx,.html,.htm,.zip"
              multiple
              className="hidden"
              onChange={e => {
                setCvFiles(Array.from(e.target.files));
                setCvUploadStatus([]);
                setAutoMapResults([]);
                setAutoMapDone(false);
              }} />
            <Button onClick={() => cvInputRef.current.click()} variant="outline" size="sm">Choose Files</Button>
          </div>

          {cvFiles.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">{cvFiles.length} file{cvFiles.length !== 1 ? 's' : ''} selected</span>
                <Button onClick={() => { setCvFiles([]); setCvUploadStatus([]); setAutoMapResults([]); setAutoMapDone(false); cvInputRef.current.value = ''; }} variant="outline" size="sm" className="text-xs">Clear</Button>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {cvFiles.map((f, i) => {
                  const st = cvUploadStatus[i];
                  return (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 text-sm ${i > 0 ? 'border-t border-gray-100' : ''} ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-base">{fileIcon(f.name)}</span>
                        <span className="font-medium text-gray-800 truncate">{f.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        {!st && <span className="text-xs text-gray-400">Pending</span>}
                        {st?.status === 'pending' && (
                          <span className="flex items-center gap-1 text-xs text-blue-600">
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            {st.chunkProgress || 'Uploading…'}
                          </span>
                        )}
                        {st?.status === 'done'    && <Check className="w-4 h-4 text-green-600" />}
                        {st?.status === 'error'   && (
                          <span className="text-xs text-red-600 max-w-[180px] truncate" title={st.error}>❌ {st.error}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button onClick={handleCvUpload} disabled={cvUploading} className="bg-green-600 w-full">
                {cvUploading
                  ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Uploading…</>
                  : `Upload ${cvFiles.length} File${cvFiles.length !== 1 ? 's' : ''} to GitHub`}
              </Button>

              {cvUploadStatus.length > 0 && !cvUploading && (
                <div className="text-sm flex items-center gap-3">
                  {cvUploadStatus.filter(s => s.status === 'done').length > 0 && (
                    <span className="text-green-700 font-medium">✅ {cvUploadStatus.filter(s => s.status === 'done').length} uploaded</span>
                  )}
                  {cvUploadStatus.some(s => s.status === 'error') && (
                    <span className="text-red-600 font-medium">{cvUploadStatus.filter(s => s.status === 'error').length} failed</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Auto-map results ── */}
          {autoMapResults.length > 0 && (
            <div className="space-y-3 border border-blue-200 rounded-xl p-4 bg-blue-50">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-blue-800">
                  🔗 Auto-match: {autoMapResults.filter(r => r.accepted).length} of {autoMapResults.length} files matched to candidates
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setAutoMapResults(prev => prev.map(r => ({ ...r, accepted: !!r.candidate })))}
                    className="text-xs text-blue-600 hover:underline">Select all with match</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => setAutoMapResults(prev => prev.map(r => ({ ...r, accepted: false })))}
                    className="text-xs text-gray-500 hover:underline">Deselect all</button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 w-7">✓</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600">File</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600">Matched Candidate</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-center font-semibold text-gray-600 w-20">Confidence</th>
                      <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 min-w-[110px]">Override Candidate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoMapResults.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border-b border-gray-100 px-3 py-2 text-center">
                          <input type="checkbox" checked={row.accepted}
                            disabled={!row.candidate}
                            onChange={e => setAutoMapResults(prev => prev.map((r, i) => i === idx ? { ...r, accepted: e.target.checked } : r))} />
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2">
                          <span className="font-mono text-gray-700 break-all">{fileIcon(row.filename)} {row.filename}</span>
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2">
                          {row.candidate
                            ? (
                              <span className="font-medium text-gray-900">
                                {row.candidate.name}
                                {row.matchType === 'direct' && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold">DIRECT</span>
                                )}
                                {row.matchType === 'fuzzy' && (
                                  <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">FUZZY</span>
                                )}
                              </span>
                            )
                            : <span className="text-gray-400 italic">No match found</span>}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2 text-center">
                          {row.candidate ? (
                            <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${
                              row.confidence >= 0.75 ? 'bg-green-100 text-green-800' :
                              row.confidence >= 0.5  ? 'bg-blue-100 text-blue-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>{Math.round(row.confidence * 100)}%</span>
                          ) : '—'}
                        </td>
                        <td className="border-b border-gray-100 px-3 py-2">
                          <select
                            value={row.candidate?.id || ''}
                            onChange={e => {
                              const found = candidates.find(c => c.id === e.target.value) || null;
                              setAutoMapResults(prev => prev.map((r, i) =>
                                i === idx ? { ...r, candidate: found, accepted: !!found, confidence: found ? 1 : 0 } : r
                              ));
                            }}
                            className="text-xs border rounded px-1 py-0.5 w-full">
                            <option value="">— override —</option>
                            {candidates.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 items-center">
                <Button
                  onClick={applyAutoMap}
                  disabled={autoMapApplying || autoMapResults.filter(r => r.accepted).length === 0}
                  className="bg-blue-600">
                  {autoMapApplying
                    ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Saving…</>
                    : `Apply ${autoMapResults.filter(r => r.accepted).length} Mapping${autoMapResults.filter(r => r.accepted).length !== 1 ? 's' : ''}`}
                </Button>
                <Button onClick={() => setAutoMapResults([])} variant="outline" size="sm">Dismiss</Button>
              </div>
            </div>
          )}

          {autoMapDone && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <Check className="w-4 h-4" /> {autoAppliedCount > 0 ? `${autoAppliedCount} CV mapping${autoAppliedCount !== 1 ? 's' : ''} saved to GitHub ✓` : 'CV mappings saved to GitHub ✓'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HRBulkImport;
